import React, { useLayoutEffect, useRef, useState } from "react";
import { usePrefersReducedMotion } from "components/emptyState/glyphMotion";

// A reel holds the ten digits four times: it spins from the previous digit, in the first round,
// to the new one in the last round, as a slot machine does.
const ROUNDS = 4;
const REEL = Array.from({ length: ROUNDS * 10 }, (_, index) => index % 10);
const LINE = "1.25em";
// The rightmost reel stops first, each next one a little later.
const DURATION_MS = 900;
const STAGGER_MS = 90;

const DigitReel = ({ digit, order, animate }) => {
    // From 0 on first display: the amount spins in when it appears.
    const previousDigit = useRef(0);
    const [position, setPosition] = useState({ index: 0, moving: false });

    useLayoutEffect(() => {
        const from = previousDigit.current;
        previousDigit.current = digit;
        const target = (ROUNDS - 1) * 10 + digit;
        if (!animate || from === digit) {
            setPosition({ index: target, moving: false });
            return undefined;
        }
        // Back to the first round without transition, then spin down to the target.
        setPosition({ index: from, moving: false });
        let frame = requestAnimationFrame(() => {
            frame = requestAnimationFrame(() => setPosition({ index: target, moving: true }));
        });
        return () => cancelAnimationFrame(frame);
    }, [digit, animate]);

    return (
        <span className="relative inline-block overflow-hidden align-bottom" style={{ height: LINE }} aria-hidden="true">
            <span
                className="flex flex-col"
                style={{
                    transform: `translateY(calc(-${position.index} * ${LINE}))`,
                    transition: position.moving
                        ? `transform ${DURATION_MS + order * STAGGER_MS}ms cubic-bezier(0.15, 0.85, 0.25, 1.05)`
                        : "none",
                }}
            >
                {REEL.map((value, index) => (
                    <span key={index} className="block text-center tabular-nums" style={{ height: LINE, lineHeight: LINE }}>
                        {value}
                    </span>
                ))}
            </span>
        </span>
    );
};

/**
 * Text whose digits roll like slot machine reels when it changes ("1 234.56" → "1 240.00");
 * the other characters (spaces, dot, currency) stay still. Screen readers get the plain text.
 * "Reduce motion": the digits change without rolling.
 */
const RollingNumber = ({ value, className = "" }) => {
    const reducedMotion = usePrefersReducedMotion();
    const text = value == null ? "" : String(value);
    const characters = text.split("");
    // Reels are keyed from the right, so the units keep their reel when the number gets longer.
    let digitOrder = 0;
    const orders = characters.map((character) => (/\d/.test(character) ? digitOrder++ : null));
    const digitCount = digitOrder;

    return (
        <span className={`inline-flex items-end tabular-nums ${className}`}>
            <span className="sr-only">{text}</span>
            {characters.map((character, index) => {
                const fromRight = characters.length - index;
                return /\d/.test(character) ? (
                    <DigitReel
                        key={`d${fromRight}`}
                        digit={Number(character)}
                        order={digitCount - 1 - orders[index]}
                        animate={!reducedMotion}
                    />
                ) : (
                    <span key={`c${fromRight}`} className="inline-block whitespace-pre" style={{ height: LINE, lineHeight: LINE }} aria-hidden="true">
                        {character}
                    </span>
                );
            })}
        </span>
    );
};

export default RollingNumber;
